import axios from "axios";
import {normalizeSnapshot} from "../digitalSigningSnapshot.util";
import {
    DigitalSigningFailure,
    DigitalSigningSession,
    DigitalSigningSignRequest,
    DigitalSigningSignResponse,
    DigitalSigningStatus
} from "./digitalSigning.types";

const BASE = '/v3/public/digitalSigning';

// Keep public calls free of JWT interceptors; tokens belong in request bodies, never request URLs.
const publicDigitalSigningApi = axios.create({
    baseURL: process.env.REACT_APP_API_ENDPOINT,
    timeout: 30000,
});

const GONE_REASONS = ['VOIDED', 'EXPIRED', 'USED'] as const;

export const isDigitalSigningFailure = (error: unknown): error is DigitalSigningFailure => (
    !!error && typeof error === 'object' && typeof (error as DigitalSigningFailure).reason === 'string'
);

const toFailure = (error: any): DigitalSigningFailure => {
    const status: number | undefined = error?.response?.status;
    const message: unknown = error?.response?.data?.message;

    if (status === 404) return {reason: 'NOT_FOUND'};
    if (status === 410) return {reason: GONE_REASONS.find(reason => reason === message) ?? 'EXPIRED'};
    if (status === 409) return {reason: 'CHANGED'};
    if (!status || status >= 500 || status === 429) return {reason: 'NETWORK'};

    return {reason: 'INVALID', message: typeof message === 'string' && message.trim() ? message : `HTTP ${status}`};
};

const attempt = async <T>(call: () => Promise<{data: T}>): Promise<T> => {
    try {
        return (await call()).data;
    } catch (error) {
        throw toFailure(error);
    }
};

type SessionResponse = {
    snapshotJson: string
    snapshotSha256: string
    expiresAt?: string
    status: DigitalSigningStatus
};

export const getDigitalSigningSession = async (token: string): Promise<DigitalSigningSession> => {
    const {snapshotJson, ...session} = await attempt<SessionResponse>(
        () => publicDigitalSigningApi.post(`${BASE}/session`, {token})
    );

    const snapshot = normalizeSnapshot(snapshotJson);
    if (!snapshot) {
        const failure: DigitalSigningFailure = {reason: 'INVALID', message: 'The confirmation could not be read'};
        throw failure;
    }

    return {...session, snapshot};
};

export const submitSignature = (request: DigitalSigningSignRequest) => (
    attempt<DigitalSigningSignResponse>(() => publicDigitalSigningApi.post(`${BASE}/sign`, request))
);
